import { useDisclosure } from "@caspeco/casper-ui-library";
import { useFilteredAvailableTimes } from "api/api-hooks/use-available-times";
import { useCancelBooking } from "api/api-hooks/use-cancel-booking";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { BookableItem } from "components/booking-modal/bookable-item";
import { BookingModal } from "components/booking-modal/booking-modal";
import ConfirmationDialog from "components/confirm-dialog";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { TrackingStepName } from "logger/user-tracking";
import queryClient from "query-client";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Time } from "store/store-types";
import { AvailableTimesSection } from "types/available-times";
import testId from "types/test-id";
import { TimeSlot } from "types/time-slot";

interface ActivityModalProps {
    section: AvailableTimesSection | null;
    setSummaryOpen: (open: boolean) => void;
    onClose: () => void;
    onConfirm?: () => void;
    isWebRule?: boolean;
    openOnMenuStep?: boolean;
}

function ActivityModal({
    section,
    setSummaryOpen,
    onClose,
    onConfirm,
    isWebRule,
    openOnMenuStep,
}: ActivityModalProps) {
    const { t } = useTranslation();
    const { bookingId } = useParams();
    const state = useBookingStateContext();
    const { handleCreateBooking } = useBookingActionsContext();
    const { data: booking } = useWebBooking();
    const cancelBooking = useCancelBooking();
    const filteredAvailableTimes = useFilteredAvailableTimes(state.date);
    const activeSection = section
        ? (filteredAvailableTimes.find((s) => s.id === section.id) ?? null)
        : null;
    const {
        isOpen: isConfirmDialogOpen,
        onClose: onConfirmDialogClose,
        onOpen: onConfirmDialogOpen,
    } = useDisclosure();

    const pendingWaitlistRef = useRef<{ times: TimeSlot[]; close: () => void } | null>(null);

    const proceedWithWaitlist = (times: TimeSlot[], close: () => void) => {
        const stateWithTimes = { ...state, times: times as Time[], isWaitList: true };
        handleCreateBooking?.(stateWithTimes);
        setSummaryOpen(false);
        close();
    };

    const handleWaitlistContinue = (times: TimeSlot[], close: () => void) => {
        if (booking?.articles && booking.articles.length > 0) {
            pendingWaitlistRef.current = { times, close };
            onConfirmDialogOpen();
            return;
        }
        proceedWithWaitlist(times, close);
    };

    const handleCancelBookingAndWaitlist = async () => {
        const pending = pendingWaitlistRef.current;
        if (!pending) return;
        await cancelBooking.mutateAsync({ systemTriggered: true });
        queryClient.removeQueries({ queryKey: ["booking", bookingId] });
        await queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
        proceedWithWaitlist(pending.times, pending.close);
        pendingWaitlistRef.current = null;
    };

    const displaySection = activeSection ?? section;
    const item: BookableItem | null = displaySection
        ? {
              id: displaySection.id,
              name: displaySection.name,
              description: displaySection.description,
              image: displaySection.image,
              activities: [displaySection],
              supportsWaitlist: true,
              continueTrackingId: "continue-select-activity-time",
              stepName: TrackingStepName.ActivityModal,
              trackingMetadata: { activityName: displaySection.name ?? undefined },
              trackingDependencies: [displaySection.id],
              filterSectionId: displaySection.id,
              filterButtonTrackingId: "open-guest-date-filter-section",
              dataTestId: testId.customizeWizard.modalwrapper,
              onAfterConfirm: onConfirm,
              hideWebRuleHeader: isWebRule,
          }
        : null;

    return (
        <>
            <BookingModal
                item={item}
                isOpen={!!section}
                onClose={onClose}
                onWaitlistContinue={handleWaitlistContinue}
                openOnMenuStep={openOnMenuStep}
            />
            <ConfirmationDialog
                secondaryButtonText={t("summary.cancel")}
                primaryButtonText={t("waitList.continue")}
                isOpen={isConfirmDialogOpen}
                title={t("waitList.bookingClearWarningTitle")}
                message={t("waitList.bookingClearWarningMessage")}
                onClose={onConfirmDialogClose}
                secondaryAction={onConfirmDialogClose}
                primaryAction={handleCancelBookingAndWaitlist}
            />
        </>
    );
}

export { ActivityModal };
